import { forwardRef } from 'react';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';

interface ToggleProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  description?: string;
  error?: string;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}

function ToggleComponent<T extends FieldValues>(
  { name, control, label, description, error, disabled = false, onChange }: ToggleProps<T>,
  ref: any
) {
  return (
    <div className="w-full">
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <div className={`flex items-center ${label || description ? 'justify-between' : 'justify-end'}`}>
            {(label || description) && (
              <div className="flex-1">
                {label && (
                  <label htmlFor={name} className="block text-sm font-medium text-gray-700">
                    {label}
                  </label>
                )}
                {description && (
                  <p className="text-xs text-gray-500 mt-1">{description}</p>
                )}
              </div>
            )}
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                {...field}
                ref={ref}
                type="checkbox"
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  onChange?.(e.target.checked);
                }}
                disabled={disabled}
                className="sr-only peer"
                id={name}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
            </label>
          </div>
        )}
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export const Toggle = forwardRef(ToggleComponent) as <T extends FieldValues>(
  props: ToggleProps<T> & { ref?: any }
) => ReturnType<typeof ToggleComponent>;
